import { t } from '@lingui/core/macro';
import { DiagnosisActionButtons } from '@src/components/diagnose/diagnosis-action-buttons';
import { KindValue } from '@src/components/value-viewer/KindValue';
import { useDiagnosisReport } from '@src/components/diagnose/use-diagnosis-report';
import { copyToClipboard, FlowpadDiagnosis, TypeId } from '@sdk';
import { useEntity } from '@sdk/react/hooks';
import { notify } from '@src/notifications';
import { DockPointer } from '@src/navigation/DockPointer';
import { useDockNavigation } from '@src/navigation/useDockNavigation';
import { Copy, Loader2 } from 'lucide-react';
import { Fragment, useCallback, useMemo } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

interface DiagnosisDetailsProps {
  /** The FlowpadDiagnosis entity id (UUID, no type prefix). */
  diagnosisId: string;
  /** The support conversation — excluded from the Forward picker (you don't forward
   *  a diagnosis back into the conversation that already holds it). */
  conversationId?: string;
  /** Called after a successful report/forward send (e.g. the modal closes). */
  onActionDone?: () => void;
  /**
   * Typography scale. `'modal'` (default) is the compact popup body; `'page'` is
   * the full-tab reading layout — larger font, looser line spacing, more air
   * between sections, to match the other full-screen entity viewers.
   */
  variant?: 'modal' | 'page';
}

interface Field {
  label: string;
  value?: string;
}

/** The four labelled diagnosis fields, in display order. */
function diagnosisFields(diag: FlowpadDiagnosis): Field[] {
  return [
    { label: t`Summary`, value: diag.summary },
    { label: t`Symptoms`, value: diag.symptoms },
    { label: t`Root cause`, value: diag.rca },
    { label: t`Fix`, value: diag.fix },
  ];
}

/**
 * The provenance rows — the same block the "Report issue" email prints under
 * `Details`. Every value is read off the record (stamped by the flow-diagnose
 * reporter at record time and carried in the share bundle), NOT recomputed here:
 * when a diagnosis is forwarded, the person reading this is on a different
 * machine, and `navigator.platform` there would describe the helper's box rather
 * than the box the issue happened on. `occurred_at` is used rather than
 * `created_date` because a receiver's install re-stamps the latter.
 */
function diagnosisDetailFields(diag: FlowpadDiagnosis, diagnosisId: string): Field[] {
  return [
    { label: t`User`, value: diag.reported_by },
    { label: t`When`, value: diag.occurred_at },
    { label: t`OS`, value: diag.os },
    { label: t`App version`, value: diag.app_version },
    { label: t`Diagnosis id`, value: diagnosisId },
  ];
}

/**
 * Assemble the full diagnosis (title + all fields + the Details block) as one
 * plain-text blob for the clipboard. Shared by the details body's Copy button and
 * the settings table's per-row Copy-all action.
 */
export function diagnosisToText(diag: FlowpadDiagnosis): string {
  const title = diag.title || diag.name || 'Diagnosis';
  const body = diagnosisFields(diag)
    .filter((f) => f.value)
    .map((f) => `${f.label}:\n${f.value}`)
    .join('\n\n');
  const details = diagnosisDetailFields(diag, diag.id)
    .filter((f) => f.value)
    .map((f) => `${f.label}: ${f.value}`)
    .join('\n');
  return [title, body, details ? `Details\n${details}` : ''].filter(Boolean).join('\n\n');
}

/**
 * The shared diagnosis body — title, the four fields (summary / symptoms / root
 * cause / fix), a Copy-all button, and the same Report/Forward buttons as a Feed
 * entry. Rendered identically by the popup (`DiagnosisReportModal`), the routed
 * full-tab viewer (`DiagnosisViewer`), and the feed's View action, so the three
 * never diverge. Report emails the team (`sendDiagnosisEmailReport`); Forward
 * attaches the diagnosis entity into the chosen conversation (`forwardDiagnosis`).
 */
export function DiagnosisDetails({
  diagnosisId,
  conversationId,
  onActionDone,
  variant = 'modal',
}: DiagnosisDetailsProps) {
  const { t } = useLingui();
  const isPage = variant === 'page';
  const { navigation } = useDockNavigation();
  const typeId = useMemo(() => (diagnosisId ? new TypeId(FlowpadDiagnosis.type, diagnosisId) : null), [diagnosisId]);
  const { data: diag, isLoading } = useEntity<FlowpadDiagnosis>(typeId, { enabled: !!typeId });

  const { report, forward, busy, reported, error: reportError } = useDiagnosisReport(diagnosisId);

  const fields: Field[] = diag ? diagnosisFields(diag) : [];
  const detailFields: Field[] = diag ? diagnosisDetailFields(diag, diagnosisId) : [];

  const handleCopy = useCallback(async () => {
    if (!diag) return;
    await copyToClipboard(diagnosisToText(diag));
    notify.success({ title: t`Diagnosis copied to clipboard` });
  }, [diag, t]);

  // "Report issue" — email the diagnosis to the Flowpad team.
  const handleReportIssue = useCallback(async () => {
    if (await report()) onActionDone?.();
  }, [report, onActionDone]);

  // "Forward" — attach the diagnosis entity into the chosen conversation.
  const handleForward = useCallback(
    async (targetConversationId: string) => {
      if (await forward(targetConversationId)) {
        // Open the conversation we just forwarded into, replacing the current view.
        navigation.openDock(DockPointer.forConversation(targetConversationId));
        onActionDone?.();
      }
    },
    [forward, navigation, onActionDone],
  );

  if (isLoading) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        <span>
          <Trans>Loading diagnosis…</Trans>
        </span>
      </div>
    );
  }

  if (!diag) {
    return (
      <p className="text-xs text-muted-foreground">
        <Trans>Diagnosis not found.</Trans>
      </p>
    );
  }

  return (
    <div className={isPage ? 'space-y-8' : 'space-y-3'}>
      <div className="flex items-center justify-end">
        <button
          type="button"
          onClick={() => void handleCopy()}
          className="flex items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
          title={t`Copy diagnosis to clipboard`}
          aria-label={t`Copy diagnosis to clipboard`}
        >
          <Copy className="h-3.5 w-3.5" />
          <Trans>Copy</Trans>
        </button>
      </div>

      <div className={isPage ? 'space-y-7' : 'space-y-3'}>
        {fields
          .filter((f) => f.value)
          .map((f) => (
            <div key={f.label} className={isPage ? 'space-y-2' : undefined}>
              <p
                className={
                  isPage
                    ? 'text-sm font-semibold uppercase tracking-wide text-muted-foreground'
                    : 'text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'
                }
              >
                {f.label}
              </p>
              <p
                className={
                  isPage
                    ? 'whitespace-pre-wrap break-words text-base leading-8 text-foreground'
                    : 'whitespace-pre-wrap break-words text-xs text-foreground'
                }
              >
                {f.value}
              </p>
            </div>
          ))}
      </div>

      {/* Provenance — the same block the report email prints under `Details`, so a
          helper who received a forwarded diagnosis sees who/when/where without
          having to ask. Values travel with the record; rows missing on a
          diagnosis are skipped rather than shown blank. */}
      <div className={isPage ? 'space-y-3 border-t pt-7' : 'space-y-2 border-t pt-3'}>
        <p
          className={
            isPage
              ? 'text-sm font-semibold uppercase tracking-wide text-muted-foreground'
              : 'text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'
          }
        >
          <Trans>Details</Trans>
        </p>
        <dl
          className={
            isPage
              ? 'grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm'
              : 'grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[11px]'
          }
        >
          {detailFields
            .filter((f) => f.value)
            .map((f) => (
              <Fragment key={f.label}>
                <dt className="text-muted-foreground">{f.label}</dt>
                <dd className="break-words font-mono text-foreground">{f.value}</dd>
              </Fragment>
            ))}
        </dl>
      </div>

      {/* The full diagnosis (findings, machine, log tails) — the same viewer a helper sees. */}
      {diag.diagnosis && (
        <div className={isPage ? 'border-t pt-7' : 'border-t pt-3'}>
          <KindValue kind="diagnosis" value={diag.diagnosis} testId="diagnosis-full" />
        </div>
      )}

      {/* Same actions as a Feed entry: Report issue emails the team (needs only the
          diagnosis), Forward posts the report into a chosen conversation. */}
      <DiagnosisActionButtons
        suggestedConversationId={conversationId}
        busy={busy}
        error={reportError}
        reported={reported}
        showDismiss={false}
        canReport={!!diagnosisId}
        diagnosisId={diagnosisId}
        diagnosisTitle={diag.title || diag.name || undefined}
        showOpenInTerminal
        onDismiss={() => onActionDone?.()}
        onReportIssue={() => void handleReportIssue()}
        onForward={(targetConversationId) => void handleForward(targetConversationId)}
        onForwardedNew={() => onActionDone?.()}
      />
    </div>
  );
}
